import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  styled,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  PlayArrow as PlayArrowIcon,
  Done as DoneIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import WorkOrderDetailManager from '../ordenes/WorkOrderDetailManager';
import { calculateIva } from '../../utils/quotationTax';

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: '#f5f5f5',
  }
});

const AgendaServicios = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Recargas posteriores a la inicial: la tabla se queda visible (antes el spinner de pantalla
  // completa reemplazaba toda la Agenda y parecía que se recargaba la página)
  const [refreshing, setRefreshing] = useState(false);
  // El gestor de programación avisa si de verdad se guardó algo; abrir y cerrar sin cambios no recarga nada
  const scheduleChangedRef = useRef(false);
  const [error, setError] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    scheduled: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  });
  const [openScheduleManagerDialog, setOpenScheduleManagerDialog] = useState(false);
  const [selectedWorkOrderForSchedules, setSelectedWorkOrderForSchedules] = useState(null);
  const [pendingHighlightDetail, setPendingHighlightDetail] = useState(null);
  // Fechas por ente ({ workOrderId: [{ entityName, scheduledDate, scheduledTimeStart }] }) --
  // para desglosar "Fecha Programada" cuando la orden tiene varios entes con fechas distintas
  // (workOrder.scheduledDate solo trae la más temprana, ver WorkOrderDetailService.updateWorkOrderDates).
  const [workOrderEntitySchedulesMap, setWorkOrderEntitySchedulesMap] = useState({});
  useEffect(() => {
    fetchWorkOrders();
    fetchStats();
  }, []);

  // Si venimos de Validación de Campos (Programas.jsx) con una orden y un campo específico a
  // resaltar (hoy solo Nivel de Riesgo), abrimos directamente el gestor de ese ente en cuanto la
  // lista de órdenes esté cargada -- mismo patrón que CatalogoEmpresas.jsx/GestionSucursales.jsx.
  useEffect(() => {
    if (!location.state?.highlightWorkOrderId) return;
    if (workOrders.length === 0) return;
    const wo = workOrders.find((w) => String(w.id) === String(location.state.highlightWorkOrderId));
    if (wo) {
      handleOpenScheduleManager(wo);
      setPendingHighlightDetail({
        entityType: location.state.highlightEntityType,
        branchId: location.state.highlightBranchId ?? null,
        fieldKey: location.state.highlightFieldKey,
      });
    }
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [workOrders, location.state]);

  const fetchWorkOrders = async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError('');

      // Obtener todas las órdenes de trabajo
      const response = await api.get('/work-orders');
      const orders = response.data.content || response.data || [];
      setWorkOrders(orders);
      fetchEntitySchedules(orders);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setError('Error al cargar las órdenes de trabajo');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Recarga solo una orden (y sus fechas por ente) en su lugar dentro de la tabla
  const refreshWorkOrder = async (workOrderId) => {
    setRefreshing(true);
    try {
      const [orderRes, detailsRes] = await Promise.all([
        api.get(`/work-orders/${workOrderId}`),
        api.get('/work-order-details/by-work-order-ids', { params: { ids: String(workOrderId) } }),
      ]);
      setWorkOrders(prev => prev.map(o => (o.id === workOrderId ? orderRes.data : o)));
      const details = Array.isArray(detailsRes.data) ? detailsRes.data : [];
      setWorkOrderEntitySchedulesMap(prev => ({
        ...prev,
        [workOrderId]: details
          .filter((d) => d.status !== 'CANCELADA')
          .map((d) => ({
            entityName: d.entityName || (d.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'),
            scheduledDate: d.scheduledDate,
            scheduledTimeStart: d.scheduledTimeStart,
            assignedInspector: d.assignedInspector
          })),
      }));
    } catch (err) {
      console.error('Error refrescando la orden:', err);
      fetchWorkOrders({ silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  // Fecha programada por ente (work_order_details) de todas las órdenes, en un solo request --
  // mismo endpoint que ya usa OrdenesTrabajoList.jsx para desglosar "Asignado a" por ente.
  const fetchEntitySchedules = async (orders) => {
    if (!orders.length) {
      setWorkOrderEntitySchedulesMap({});
      return;
    }
    try {
      const response = await api.get('/work-order-details/by-work-order-ids', {
        params: { ids: orders.map((o) => o.id).join(',') }
      });
      const details = Array.isArray(response.data) ? response.data : [];
      const byOrderId = {};
      details
        .filter((d) => d.status !== 'CANCELADA')
        .forEach((d) => {
          if (!byOrderId[d.workOrderId]) byOrderId[d.workOrderId] = [];
          byOrderId[d.workOrderId].push({
            entityName: d.entityName || (d.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'),
            scheduledDate: d.scheduledDate,
            scheduledTimeStart: d.scheduledTimeStart,
            assignedInspector: d.assignedInspector
          });
        });
      setWorkOrderEntitySchedulesMap(byOrderId);
    } catch (err) {
      console.error('Error cargando fechas por ente:', err);
      setWorkOrderEntitySchedulesMap({});
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/work-orders/statistics');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching work order statistics:', error);
    }
  };

  // scheduledDate llega como "YYYY-MM-DD" (LocalDate, sin hora). `new Date(str)` lo interpreta
  // como medianoche UTC -- en huso horario de México (UTC-6) eso cae un día antes al convertir a
  // local, mostrando la fecha equivocada. Parseamos el string directo para evitar esa conversión.
  const formatScheduledDate = (dateString) => {
    if (!dateString) return null;
    const [y, m, d] = dateString.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX');
  };

  const getEstadoChip = (workOrder) => {
    const colores = {
      'PENDIENTE': 'warning',
      'ASIGNADA': 'secondary',
      'PROGRAMADA': 'info',
      'EN_PROGRESO': 'primary',
      'COMPLETADA': 'success',
      'CANCELADA': 'error'
    };
    
    const labels = {
      'PENDIENTE': 'Pendiente de Asignación',
      'ASIGNADA': 'Asignada',
      'PROGRAMADA': 'Programada',
      'EN_PROGRESO': 'En Progreso',
      'LISTA_PARA_REVISION': 'Lista para Revisión',
      'COMPLETADA': 'Completada',
      'CANCELADA': 'Cancelada'
    };
    
    return (
      <Chip 
        label={labels[workOrder.status] || workOrder.status} 
        color={colores[workOrder.status] || 'default'} 
        size="small" 
        variant="outlined"
      />
    );
  };

  const workOrdersFiltradas = workOrders.filter(workOrder => {
    if (filtroEstado === 'todos') return true;
    return workOrder.status === filtroEstado.toUpperCase();
  });

  // Actualizar estado de orden de trabajo
  const updateWorkOrderStatus = async (workOrderId, newStatus) => {
    try {
      setError('');
      
      await api.put(`/work-orders/${workOrderId}/status`, { status: newStatus });
      
      // Actualizar la lista local
      setWorkOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === workOrderId 
            ? { ...order, status: newStatus }
            : order
        )
      );
      
      // Refrescar estadísticas
      fetchStats();
      
    } catch (error) {
      console.error('Error updating work order status:', error);
      setError('Error al actualizar el estado de la orden de trabajo');
    }
  };

  // Abrir gestor de schedules por ente
  const handleOpenScheduleManager = (workOrder) => {
    scheduleChangedRef.current = false;
    setSelectedWorkOrderForSchedules(workOrder);
    setOpenScheduleManagerDialog(true);
  };

  // Cerrar gestor de schedules
  const handleCloseScheduleManager = () => {
    const changedOrderId = scheduleChangedRef.current ? selectedWorkOrderForSchedules?.id : null;
    scheduleChangedRef.current = false;
    setOpenScheduleManagerDialog(false);
    setSelectedWorkOrderForSchedules(null);
    if (changedOrderId) {
      refreshWorkOrder(changedOrderId);
      fetchStats();
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Agenda de Servicios - Órdenes de Trabajo
        </Typography>
        <IconButton
          onClick={() => {
            fetchWorkOrders({ silent: true });
            fetchStats();
          }}
          disabled={loading || refreshing}
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'rotate(180deg)',
              transition: 'all 0.3s ease-in-out'
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666'
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Explicación contextual */}
      <Alert
        severity="info"
        icon={<EventIcon fontSize="inherit" />}
        sx={{ mb: 2, borderRadius: 2, alignItems: 'center' }}
      >
        <strong>¿Qué son las órdenes de trabajo?</strong> Cada orden se genera automáticamente cuando
        el cliente <strong>acepta una cotización</strong>. Aquí puedes programar fechas, asignar
        inspectores y dar seguimiento al avance de cada servicio.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cards de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Órdenes
                  </Typography>
                  <Typography variant="h4" component="div">
                    {stats.total}
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pendientes
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <ScheduleIcon sx={{ fontSize: 40, color: '#ff9800' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    En Progreso
                  </Typography>
                  <Typography variant="h4" component="div" color="primary.main">
                    {stats.inProgress}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completadas
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, color: '#4caf50' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filtrar por estado</InputLabel>
          <Select
            value={filtroEstado}
            label="Filtrar por estado"
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <MenuItem value="todos">Todas las órdenes</MenuItem>
            <MenuItem value="pendiente">Pendientes</MenuItem>
            <MenuItem value="asignada">Asignadas</MenuItem>
            <MenuItem value="programada">Programadas</MenuItem>
            <MenuItem value="en_progreso">En Progreso</MenuItem>
            <MenuItem value="completada">Completadas</MenuItem>
            <MenuItem value="cancelada">Canceladas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabla de órdenes de trabajo */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {refreshing && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Número de Orden
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Cliente
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Cotización
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  Servicio
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center">
                  Estado
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center">
                  Fecha Programada
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="right">
                  Monto (con IVA)
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workOrdersFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No hay órdenes de trabajo disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                workOrdersFiltradas.map((workOrder) => (
                  <StyledTableRow key={workOrder.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, color: '#1976d2' }} />
                        <Typography variant="body1" fontWeight="medium">
                          {workOrder.orderNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {workOrder.companyName && (
                        <Typography variant="body2" fontWeight="medium">
                          {workOrder.companyName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="textSecondary" display="block">
                        {workOrder.clientName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {workOrder.clientEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {workOrder.quotationFolio ? (
                        <Chip 
                          label={workOrder.quotationFolio}
                          size="small"
                          sx={{ 
                            bgcolor: '#fff3e0', 
                            color: '#e65100',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate(`/cotizaciones/new-quotation?edit=${workOrder.quotationId}`)}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                      )}
                      {workOrder.replacedQuotationFolio && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#757575', mt: 0.5 }}>
                          Reemplaza a {workOrder.replacedQuotationFolio}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {workOrder.serviceType}
                      </Typography>
                      {workOrder.businessSectorName && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Giro: {workOrder.businessSectorName}
                        </Typography>
                      )}
                      {/* Las OTs anteriores a V_89 guardaban aquí el ID del giro: no mostrar ese número */}
                      {workOrder.attentionGroup && !/^\d+$/.test(String(workOrder.attentionGroup).trim()) && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          Grupo de atención: {workOrder.attentionGroup}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {getEstadoChip(workOrder)}
                    </TableCell>
                    <TableCell align="center">
                      {(workOrderEntitySchedulesMap[workOrder.id] || []).length > 0 ? (
                        <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {workOrderEntitySchedulesMap[workOrder.id].map((d, i) => (
                            <Box key={`${workOrder.id}-${i}`}>
                              {workOrderEntitySchedulesMap[workOrder.id].length > 1 && (
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {d.entityName}:{' '}
                                </Typography>
                              )}
                              <Typography variant="caption" color="textSecondary">
                                {formatScheduledDate(d.scheduledDate) || 'Sin programar'}
                                {d.scheduledTimeStart ? ` · ${d.scheduledTimeStart}` : ''}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontStyle: d.assignedInspector ? 'normal' : 'italic' }}>
                                {d.assignedInspector || 'Sin ejecutivo asignado'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2" color="textSecondary">
                            {formatScheduledDate(workOrder.scheduledDate) || 'Sin programar'}
                          </Typography>
                          {workOrder.scheduledTimeStart && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {workOrder.scheduledTimeStart}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* totalAmount de la OT es el subtotal cotizado sin IVA; se muestra igual que el PDF */}
                      {(() => {
                        const { subtotal, iva, totalWithIva } = calculateIva(workOrder.totalAmount || 0);
                        const money = (v) => `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        return (
                          <>
                            <Typography variant="body1" fontWeight="medium" color="success.main">{money(totalWithIva)}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {money(subtotal)} + IVA {money(iva)}
                            </Typography>
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {/* Botón para programar schedules por ente */}
                        {(workOrder.status === 'PENDIENTE' || workOrder.status === 'ASIGNADA' || workOrder.status === 'PROGRAMADA' || workOrder.status === 'EN_PROGRESO') && (
                          <Tooltip title="Programar inspecciones por ente (Matriz y Sucursales)" arrow>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleOpenScheduleManager(workOrder)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'secondary.main',
                                '&:hover': {
                                  backgroundColor: 'secondary.light',
                                  opacity: 0.8
                                }
                              }}
                            >
                              <CalendarTodayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {workOrder.status === 'PROGRAMADA' && (
                          <Tooltip 
                            title={
                              !workOrder.scheduledDate 
                                ? "Debe programar la fecha antes de iniciar la orden" 
                                : "Iniciar orden de trabajo"
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => updateWorkOrderStatus(workOrder.id, 'EN_PROGRESO')}
                                disabled={!workOrder.scheduledDate}
                                sx={{
                                  border: '1px solid',
                                  borderColor: !workOrder.scheduledDate ? 'grey.400' : 'success.main',
                                  '&:hover': {
                                    backgroundColor: 'success.light',
                                    opacity: 0.8
                                  },
                                  '&:disabled': {
                                    borderColor: 'grey.400',
                                    color: 'grey.400',
                                    cursor: 'not-allowed'
                                  }
                                }}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {workOrder.status === 'EN_PROGRESO' && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => updateWorkOrderStatus(workOrder.id, 'COMPLETADA')}
                            title="Completar orden de trabajo"
                            sx={{
                              border: '1px solid',
                              borderColor: 'success.main',
                              '&:hover': {
                                backgroundColor: 'success.light',
                                opacity: 0.8
                              }
                            }}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {workOrdersFiltradas.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Mostrando {workOrdersFiltradas.length} órdenes de trabajo
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Última actualización: {new Date().toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      )}

      {/* Dialog para Gestor de Schedules por Ente */}
      <Dialog 
        open={openScheduleManagerDialog} 
        onClose={handleCloseScheduleManager}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon />
            <Box>
              <Typography variant="h6">Programación de Inspecciones por Ente</Typography>
              {selectedWorkOrderForSchedules && (
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Orden: {selectedWorkOrderForSchedules.orderNumber} - {selectedWorkOrderForSchedules.clientName}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {selectedWorkOrderForSchedules && (
            <WorkOrderDetailManager
              workOrder={selectedWorkOrderForSchedules}
              onUpdate={handleCloseScheduleManager}
              onDataChanged={() => { scheduleChangedRef.current = true; }}
              highlightDetail={pendingHighlightDetail}
              onHighlightConsumed={() => setPendingHighlightDetail(null)}
            />
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseScheduleManager}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
              }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgendaServicios;
